// A store type as a form (02-DESIGN/03-console/02-forms-from-schemas.md
// § where it applies: a store type; 02-DESIGN/10-state/01-stores-types-and-
// instances.md § a type): the record's four facets as four parts — what an
// item holds, its history, what it may hold, its changes — read from the
// record and written back into it. Pure, so that a record read and written
// back is the record it was. A record whose schemas the form cannot read is
// not read at all; the reason says why and the form opens on JSON.

import {
  newId,
  problems as fieldProblems,
  schemaOf,
  shapeOf,
  type Field,
  type Problem,
  type Shape,
} from "../../ui/schema/shape.ts";
import { Effect, HistoryPolicy, validName, type OperationDef, type TypeRecord } from "../../wire/state.ts";

/** ChildRow is one line of *May hold*: a name and the type that may live under it. */
export interface ChildRow {
  id: string;
  name: string;
  type: string;
}

/** Data says what a change's data is: anything, or these fields. */
export type Data = "anything" | "fields";

/** ChangeRow is one change the type admits: its name, what it does, and its data. */
export interface ChangeRow {
  id: string;
  name: string;
  /** The effect as written; absent reads as recorded only and stays absent. */
  effect?: Effect;
  data: Data;
  /** The data's fields when `data` is `fields`, with the schema's own keywords. */
  shape: Shape;
  /** Whether the change carried no schema at all, so that *anything* writes none back. */
  bare: boolean;
  /** The change's other keys, kept as written. */
  extra: Record<string, unknown>;
}

/** TypeDraft is a type's record as the form holds it. */
export interface TypeDraft {
  shape: Shape;
  /** Whether the record carried a schema, so that no fields writes none back. */
  hasSchema: boolean;
  history?: HistoryPolicy;
  children: ChildRow[];
  changes: ChangeRow[];
}

const ANYTHING = { type: "object" };

function isAnything(schema: unknown): boolean {
  return (
    typeof schema === "object" &&
    schema !== null &&
    !Array.isArray(schema) &&
    Object.keys(schema).length === 1 &&
    (schema as Record<string, unknown>).type === "object"
  );
}

/** blankType is a new type: no fields, compactable, holding nothing, admitting no change. */
export function blankType(): TypeDraft {
  return { shape: { fields: [], extra: {} }, hasSchema: false, children: [], changes: [] };
}

/** blankChange is a new change: no name, updates the item, data anything. */
export function blankChange(): ChangeRow {
  return {
    id: newId(),
    name: "",
    effect: Effect.merge,
    data: "anything",
    shape: { fields: [], extra: {} },
    bare: false,
    extra: {},
  };
}

/** typeDraftOf reads a type's record into the form, or the reason the form cannot draw it. */
export function typeDraftOf(rec: TypeRecord): TypeDraft | { reason: string } {
  const shape = shapeOf(rec.schema);
  if ("reason" in shape) {
    return { reason: `The item's shape is shown as JSON: ${shape.reason}.` };
  }
  const changes: ChangeRow[] = [];
  for (const [name, def] of Object.entries(rec.operations ?? {})) {
    const { schema, effect, ...extra } = def as OperationDef & Record<string, unknown>;
    const row: ChangeRow = {
      id: newId(),
      name,
      effect,
      data: "anything",
      shape: { fields: [], extra: {} },
      bare: schema === undefined,
      extra,
    };
    if (schema !== undefined && !isAnything(schema)) {
      const s = shapeOf(schema);
      if ("reason" in s) {
        return { reason: `The data of ${name} is shown as JSON: ${s.reason}.` };
      }
      row.data = "fields";
      row.shape = s;
    }
    changes.push(row);
  }
  return {
    shape,
    hasSchema: rec.schema !== undefined,
    history: rec.history,
    children: Object.entries(rec.children ?? {}).map(([name, type]) => ({ id: newId(), name, type })),
    changes,
  };
}

/** typeRecordOf writes the form back into a type's record. */
export function typeRecordOf(d: TypeDraft): TypeRecord {
  const rec: TypeRecord = {};
  if (d.hasSchema || d.shape.fields.length > 0) {
    rec.schema = schemaOf(d.shape);
  }
  if (d.history !== undefined) {
    rec.history = d.history;
  }
  if (d.children.length > 0) {
    rec.children = Object.fromEntries(d.children.map((c) => [c.name.trim(), c.type]));
  }
  if (d.changes.length > 0) {
    const ops: Record<string, OperationDef> = {};
    for (const c of d.changes) {
      const def: OperationDef & Record<string, unknown> = { ...c.extra };
      if (c.data === "fields") {
        def.schema = schemaOf(c.shape);
      } else if (!c.bare) {
        def.schema = { ...ANYTHING };
      }
      if (c.effect !== undefined) {
        def.effect = c.effect;
      }
      ops[c.name.trim()] = def;
    }
    rec.operations = ops;
  }
  return rec;
}

/** typeProblems lists what the form can see is wrong, each beside its row. */
export function typeProblems(d: TypeDraft): Problem[] {
  const out: Problem[] = [...fieldProblems(d.shape.fields)];
  const children = new Set<string>();
  for (const c of d.children) {
    const n = c.name.trim();
    if (!validName(n)) {
      out.push({ id: c.id, what: "A name is lowercase letters, digits and hyphens." });
    } else if (children.has(n)) {
      out.push({ id: c.id, what: `${n} is already listed.` });
    }
    children.add(n);
    if (!validName(c.type)) {
      out.push({ id: c.id, what: "Choose the type that may live under it." });
    }
  }
  const changes = new Set<string>();
  for (const c of d.changes) {
    const n = c.name.trim();
    if (n === "") {
      out.push({ id: c.id, what: "A change needs a name, like status.set." });
    } else if (changes.has(n)) {
      out.push({ id: c.id, what: `Another change is already called ${n}.` });
    }
    changes.add(n);
    if (c.data === "fields") {
      out.push(...fieldProblems(c.shape.fields));
    }
  }
  return out;
}

/** HISTORY_WORDS and EFFECT_WORDS say a type's history and a change's effect on the page. */
export const HISTORY_WORDS = {
  [HistoryPolicy.compactable]: "May be compacted",
  [HistoryPolicy.full]: "Kept whole",
} as const;

export const EFFECT_WORDS = {
  [Effect.merge]: "Updates the item",
  [Effect.none]: "Recorded only",
} as const;

export type { Field };

/** fieldsLine is a type's fields counted, the first few named: *4 fields: title, body, state…*. */
export function fieldsLine(names: readonly string[]): string {
  const n = names.length;
  const shown = names.filter((x) => x !== "").slice(0, 3);
  const rest = n > shown.length ? "…" : "";
  return `${String(n)} ${n === 1 ? "field" : "fields"}${shown.length > 0 ? `: ${shown.join(", ")}${rest}` : ""}`;
}
